import { Switch } from '@headlessui/react';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  ALL_WEEK_DAYS,
  HourQueryParams,
  IHour,
  IHourPost,
  Office,
  WeekDayNames,
  parseTimeOnly
} from '@gettactic/api';
import { IResource, parseDateOnlyString } from '@gettactic/api';
import { api } from '@/lib/api/api';
import { classNames } from '@/lib/classNames';
import { toEntity } from '@/lib/utils';
import { DEFAULT_WEIGHT_RULE } from '@/lib/validation/hour';
import { Button, DatePicker, Input, Label, Modal } from '@/components/basic';
import FormErrorMessage from '@/components/basic/FormMessages/FormErrorMessage';
import { RESOURCES_TEXT } from '@/components/partial/Cards/Resources/ListResources';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from '@/hooks/useCustomParkingVerbiage';
import { capitalize } from '@gettactic/helpers';

type Props = {
  hours?: IHour[];
  office: Office;
  resources?: IResource[];
  onClose: () => void;
  invalidateQuery: () => Promise<void>;
};
type FormDataShape = {
  name: string;
  days: WeekDayNames[];
  is_available: boolean;
  start_time: Date;
  end_time: Date;
  since?: Date;
  until?: Date;
};

function defaultValues(hour?: IHour): FormDataShape {
  if (hour) {
    return {
      name: hour.name ?? '',
      days: hour.days ?? [],
      is_available: hour.is_available,
      start_time: parseTimeOnly(hour.start_time),
      end_time: parseTimeOnly(hour.end_time),
      since: hour.since ? parseDateOnlyString(hour.since) : undefined,
      until: hour.until ? parseDateOnlyString(hour.until) : undefined
    };
  }
  const startTime = parseTimeOnly('00:00:00.000');
  const endTime = parseTimeOnly('23:59:59.000');
  return {
    name: '',
    days: [...ALL_WEEK_DAYS],
    is_available: false,
    start_time: startTime,
    end_time: endTime,
    since: new Date(),
    until: new Date()
  };
}

export function is24Hours(start: Date, end: Date) {
  return (
    format(start, 'hh:mm aa') === '12:00 AM' &&
    format(end, 'hh:mm aa') === '11:59 PM'
  );
}

export default function CreateOrEditHourRule({
  office,
  resources,
  onClose,
  invalidateQuery,
  hours
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    register,
    setValue,
    watch,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    // TODO: Replace with Zod
    // resolver: yupResolver(schemaHourRule),
    defaultValues: defaultValues(hours ? hours[0] : undefined)
  });
  const start_time = watch('start_time');
  const end_time = watch('end_time');
  const advancedHours = !is24Hours(start_time, end_time);
  const isAvailable = watch('is_available');

  const onSubmit = async (data: FormDataShape) => {
    // Validate required fields
    if (!data.name || data.name.trim() === '') {
      toast.error('Name is required');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const body: IHourPost = {
        name: data.name,
        type: 'rule',
        days: data.days,
        is_available: data.is_available,
        start_time: format(data.start_time, 'HH:mm:ss'),
        end_time: format(data.end_time, 'HH:mm:ss'),
        since: data.since
          ? format(data.since, 'yyyy-MM-dd') + 'T00:00:00.000Z'
          : null,
        until: data.until
          ? format(data.until, 'yyyy-MM-dd') + 'T23:59:59.000Z'
          : null,
        weight: DEFAULT_WEIGHT_RULE
      };
      const queryParams: HourQueryParams = {
        tz: office.time_zone
      };
      const res = hours
        ? await Promise.all(
            hours.map(async (hour) =>
              api.client.hours.put(office.id, hour.id, body, queryParams)
            )
          )
        : resources
          ? await Promise.all(
              resources.map(
                async (resource) =>
                  await api.client.hours.createForResource(
                    resource.id,
                    body,
                    queryParams
                  )
              )
            )
          : [await api.client.hours.create(office.id, body, queryParams)];

      if (res.find((x) => !!x.error)) {
        console.error('Res: ', res);
        throw new Error('Error saving');
      }
      toast.success(hours ? 'Rule updated' : 'Rule created');
      onClose();
    } catch (e) {
      toast.error("Sorry we couldn't save the rule");
    }
    setLoading(false);
    invalidateQuery();
  };

  const sharedHour = hours ? hours[0] : null;

  const customParkingVerbiage = useCustomParkingVerbiage();

  const resourceTypeText = useMemo(() => {
    const resourceType = resources?.[0]?.type;
    let text = 'Resource';
    if (!resourceType) {
      return text;
    }
    const toEntityType = toEntity(resourceType);
    if (toEntityType) {
      text = RESOURCES_TEXT[toEntityType].individual;
    }

    const isParking = toEntityType === 'parking';
    if (customParkingVerbiage && isParking) {
      text = capitalize(customParkingVerbiage);
    }
    return text;
  }, [resources?.[0]?.type, customParkingVerbiage]);

  return (
    <form
      id="createOrEditHourRule"
      className="flex flex-auto flex-col overflow-hidden"
      onSubmit={(ev) => {
        ev.stopPropagation();
        handleSubmit(onSubmit)(ev);
      }}
    >
      <div>
        <div className="flex flex-auto flex-col overflow-y-auto">
          <div className="mt-4 grid grid-cols-12 gap-6">
            <div className="col-span-12 flex items-center justify-between">
              <div className="ml-1 text-base  font-medium text-gray-800">
                {resources
                  ? `${resourceTypeText} Availability`
                  : 'Office Availability'}
              </div>
              <div className="flex items-center">
                <span
                  className={classNames(
                    ' px-2',
                    !isAvailable ? 'font-bold' : ''
                  )}
                >
                  Closed
                </span>
                <Switch
                  checked={isAvailable}
                  onChange={() => setValue('is_available', !isAvailable)}
                  className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <span className="sr-only">Use setting</span>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute h-full w-full rounded-md bg-white"
                  />
                  <span
                    aria-hidden="true"
                    className={classNames(
                      isAvailable ? 'bg-primary' : 'bg-gray-200',
                      'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out'
                    )}
                  />
                  <span
                    aria-hidden="true"
                    className={classNames(
                      isAvailable ? 'translate-x-5' : 'translate-x-0',
                      'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out'
                    )}
                  />
                </Switch>
                <span
                  className={classNames(
                    ' px-2',
                    isAvailable ? 'font-bold' : ''
                  )}
                >
                  Open
                </span>
              </div>
            </div>

            <div className="col-span-12">
              <Label htmlFor="name" className="px-1">
                <div className="text-base text-gray-800">Name</div>
                <Input
                  variant='v2Simple'               
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  placeholder="Ex. New Year's Day"
                  // required
                />
              </Label>
              <FormErrorMessage errors={errors} fieldName={'name'} />
            </div>
            <div className="col-span-12 mb-2 ">
              <div className="col-span-12 flex justify-between space-x-4 px-1">
                <div className="w-full">
                  <Label htmlFor="since" className="block text-gray-800">
                    Start Date
                  </Label>
                  <Controller
                    control={control}
                    rules={{ required: true }}
                    name="since"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        selected={value}
                        name="since"
                        className="input w-full rounded-md"
                        calendarClassName="tailwind-datepicker"
                        timeCaption="Since"
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full">
                  <Label htmlFor="until" className="block">
                    End Date
                  </Label>
                  <Controller
                    control={control}
                    rules={{ required: true }}
                    name="until"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        selected={value}
                        name="until"
                        className="input w-full rounded-md"
                        calendarClassName="tailwind-datepicker"
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="col-span-12">
                <FormErrorMessage errors={errors} fieldName={'since'} />
                <FormErrorMessage errors={errors} fieldName={'until'} />
              </div>
            </div>

            <div className="col-span-12">
              <div className="col-span-12">
                <div className="flex h-16 items-center justify-between pl-2">
                  <div className="flex-1 flex items-center">
                    <Input
                      type="checkbox"
                      checked={!advancedHours}
                      id={`24hs_${sharedHour?.id ?? 'new'}`}
                      onChange={(ev) => {
                        if (ev.currentTarget.checked) {
                          setValue('start_time', defaultValues().start_time);
                          setValue('end_time', defaultValues().end_time);
                        } else {
                          setValue('start_time', parseTimeOnly('09:00:00.000'));
                          setValue('end_time', parseTimeOnly('17:00:00.000'));
                        }
                      }}
                      className="mr-2 shadow-none"
                    />
                    <Label htmlFor={`24hs_${sharedHour?.id ?? 'new'}`}>
                      <span
                        className={classNames(
                          '',
                          advancedHours ? '' : 'font-bold'
                        )}
                      >
                        All day
                      </span>
                    </Label>
                  </div>

                  {advancedHours ? (
                    <div className="flex-1 flex items-center">
                      <Label htmlFor="start_time" className="flex items-center">
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="start_time"
                          render={({ field: { onChange, value } }) => (
                            <DatePicker
                              selected={value}
                              className="input ml-2 w-[125px] rounded-md"
                              calendarClassName="tailwind-timepicker"
                              wrapperClassName="tailwind-timepicker-wrapper"
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={30}
                              timeCaption="Start"
                              dateFormat="h:mm aa"
                              onChange={onChange}
                            />
                          )}
                        />
                      </Label>
                      <div className="mx-4 w-4 border-b border-gray-300" />
                      <Label htmlFor="start_time" className="flex items-center">
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="end_time"
                          render={({ field: { onChange, value } }) => (
                            <DatePicker
                              selected={value}
                              className="input w-[125px] rounded-md"
                              calendarClassName="tailwind-timepicker"
                              wrapperClassName="tailwind-timepicker-wrapper"
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={30}
                              timeCaption="End"
                              dateFormat="h:mm aa"
                              onChange={onChange}
                            />
                          )}
                        />
                      </Label>
                    </div>
                  ) : null}
                </div>
              </div>

              <FormErrorMessage errors={errors} fieldName={'start_time'} />
              <FormErrorMessage errors={errors} fieldName={'end_time'} />
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          layout="squared"
          variant="white"
          font="boldGray"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          className="mt-4 w-full sm:mt-0 sm:ml-3 sm:w-auto"
          layout="squared"
          variant="primary"
          type="submit"
          form="createOrEditHourRule"
          formTarget="createOrEditHourRule"
          disabled={loading}
        >
          {loading ? <LoadingSpinner color="text-white" /> : <span>Save</span>}
        </Button>
      </Modal.Footer>
    </form>
  );
}
